import { Constants } from "config/constants";
import isAnnualReturnsDueAfterCompanyStruckOff from "utils/annualDueDateAfterCompanyStruckOff";

const convertToCarmelCase = (str) => {
  return str
    .toLowerCase() // make entire string lowercase
    .split(" ") // split by spaces
    .map((word, index) => {
      if (index === 0) return word; // first word stays lowercase
      if (word.includes("cr") || word.includes("bof"))
        return word.toUpperCase(); // for BOF and CR forms
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join("");
};

const buildForm = (formNamesArray, transaction) => {
  let formsArray = [...formNamesArray, "BRS Filing Receipt"];
  if (
    (transaction?.changeType ===
      Constants.TRANSACTION_CHANGE_TYPES.NOTICE_TO_CANCEL_CONVERSION &&
      transaction?.status ===
        Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.AWAITING_COURT_ORDER) ||
    transaction?.fileUploads?.courtOrder
  ) {
    formsArray = [...formsArray, "Court Order"];
  }

  if (
    transaction?.changeType === Constants.TRANSACTION_CHANGE_TYPES.STRIKE_OFF &&
    transaction?.status ===
      Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.INTENDED_DISSOLUTION
  ) {
    formsArray = [...formsArray, "Notice of Intended Dissolution"];
  }

  if (
    transaction?.changeType === Constants.TRANSACTION_CHANGE_TYPES.STRIKE_OFF &&
    transaction?.status ===
      Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.DISSOLUTION_NOTICE
  ) {
    formsArray = [...formsArray, "Notice of Dissolution"];
  }

  if (
    (transaction?.changeType ===
      Constants.TRANSACTION_CHANGE_TYPES.STRIKE_OFF &&
      transaction?.status ===
        Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.DRAFT &&
      transaction?.approvalType ===
        Constants.TRANSACTION_APPROVAL_TYPES.BOARD_SHAREHOLDER_APPROVAL) ||
    (transaction?.changeType ===
      Constants.TRANSACTION_CHANGE_TYPES.STRIKE_OFF &&
      transaction?.status ===
        Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_REVIEW &&
      transaction?.approvalType ===
        Constants.TRANSACTION_APPROVAL_TYPES.BOARD_SHAREHOLDER_APPROVAL &&
      !(
        transaction?.fileUploads?.noticeOfDissolution &&
        transaction?.fileUploads?.noticeOfIntendedDissolution
      ))
  ) {
    formsArray = [...formNamesArray, "Form CR19", "BRS Filing Receipt"];
  }

  if (
    transaction?.changeType === Constants.TRANSACTION_CHANGE_TYPES.STRIKE_OFF &&
    transaction?.status ===
      Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.REGISTRAR_REVIEW &&
    transaction?.fileUploads?.noticeOfDissolution &&
    transaction?.fileUploads?.noticeOfIntendedDissolution
  ) {
    const includeFormCR19 = transaction?.fileUploads?.formCR19;
    formsArray = [
      ...formNamesArray,
      ...(includeFormCR19 ? ["Form CR19"] : []),
      "BRS Filing Receipt",
      "Notice of Intended Dissolution",
      "Notice of Dissolution",
    ];
  }

  return formsArray.map((formName) => ({
    label: formName,
    name: convertToCarmelCase(formName),
    accept: "application/pdf",
  }));
};

const getCommonAppointmentForms = () => {
  return ["Form CR6", "Form CR8", "Extract of Board Resolution"];
};

/** ChangeTypes */

const appointment = (transaction) => {
  const { applicationType } = transaction;
  switch (applicationType) {
    case Constants.APPLICANT_CAPACITIES.DIRECTOR:
    case Constants.APPLICANT_CAPACITIES.DIRECTOR_SHAREHOLDER:
      return buildForm(getCommonAppointmentForms());
    case Constants.APPLICANT_CAPACITIES.ALTERNATE_DIRECTOR:
      return buildForm([
        ...getCommonAppointmentForms(),
        "Consent to Act as Alternate Director",
      ]);
    case Constants.APPLICANT_CAPACITIES.COMPANY_SECRETARY:
      return buildForm([
        ...getCommonAppointmentForms(),
        "Consent to Act as Company Secretary",
      ]);
    case Constants.APPLICANT_CAPACITIES.AUTHORIZED_PERSON:
      return buildForm([
        ...getCommonAppointmentForms(),
        "Consent to Act as Authorized Person",
      ]);
    case Constants.APPLICANT_CAPACITIES.AUDITOR:
      return buildForm([
        ...getCommonAppointmentForms(),
        "Form CR19",
        "Concent to Act as an Auditor",
      ]);
    case Constants.APPLICANT_CAPACITIES.ADMINISTRATOR:
      return buildForm([
        "Form CR45",
        "Form CR8",
        "Form CR19",
        "Concent to Act as an Administrator",
        "Extract of Board Resolution",
      ]);
    case Constants.APPLICANT_CAPACITIES.SHAREHOLDER:
      return buildForm([
        "Form CR9",
        "Cessation Doc",
        "Extract of Board Resolution",
      ]);
  }
};

const buildPostalRegisteredForms = (changedFields) => {
  let forms = [];
  if (
    changedFields &&
    Object.keys(changedFields).length > 0 &&
    (Object.keys(changedFields).includes("names") ||
      Object.keys(changedFields).includes("identificationDetails") ||
      Object.keys(changedFields).includes("electronicAddress") ||
      Object.keys(changedFields).includes("postalAddress"))
  ) {
    forms.push("Form CR7");
  }
  if (
    changedFields &&
    Object.keys(changedFields).length > 0 &&
    Object.keys(changedFields).includes("residentialAddressDetails")
  ) {
    forms.push("Form CR8");
  }
  forms.push("Extract of Board Resolution");
  return forms;
};

const buildCSPostalRegisteredForms = (changedFields) => {
  let forms = [];
  if (
    changedFields &&
    Object.keys(changedFields).length > 0 &&
    (Object.keys(changedFields).includes("names") ||
      Object.keys(changedFields).includes("identificationDetails") ||
      Object.keys(changedFields).includes("electronicAddress") ||
      Object.keys(changedFields).includes("postalAddress"))
  ) {
    forms.push("Form CR11");
  }
  if (
    changedFields &&
    Object.keys(changedFields).length > 0 &&
    Object.keys(changedFields).includes("residentialAddressDetails")
  ) {
    forms.push("Form CR12");
  }
  forms.push("Extract of Board Resolution");
  return forms;
};

const changeOfParticulars = (transaction, changedFields = {}) => {
  const { applicationType } = transaction;
  switch (applicationType) {
    case Constants.APPLICANT_CAPACITIES.DIRECTOR:
      return buildForm(buildPostalRegisteredForms(changedFields));
    case Constants.APPLICANT_CAPACITIES.ALTERNATE_DIRECTOR:
    case Constants.APPLICANT_CAPACITIES.AUTHORIZED_PERSON:
    case Constants.APPLICANT_CAPACITIES.AUDITOR:
    case Constants.APPLICANT_CAPACITIES.ADMINISTRATOR:
    case Constants.APPLICANT_CAPACITIES.DIRECTOR_SHAREHOLDER:
      return buildForm(["Form CR7", "Form CR8", "Extract of Board Resolution"]);
    case Constants.APPLICANT_CAPACITIES.COMPANY_SECRETARY:
      return buildForm(buildCSPostalRegisteredForms(changedFields));
    case Constants.APPLICANT_CAPACITIES.SHAREHOLDER:
    case Constants.APPLICANT_CAPACITIES.JOINT_SHAREHOLDER:
      return buildForm(buildPostalRegisteredForms(changedFields));
    case Constants.APPLICANT_CAPACITIES.BENEFICIAL_OWNER:
      return buildForm(["Form BOF2", "Extract of Board Resolution"]);
  }
};

const cessation = (transaction) => {
  const { applicationType } = transaction;
  switch (applicationType) {
    case Constants.APPLICANT_CAPACITIES.DIRECTOR:
      return buildForm([
        "Form CR9",
        "Cessation Doc",
        "Extract of Board Resolution",
      ]);
    case Constants.APPLICANT_CAPACITIES.ALTERNATE_DIRECTOR:
      return buildForm([
        "Form CR9",
        "Cessation Doc",
        "Extract of Board Resolution",
      ]);
    case Constants.APPLICANT_CAPACITIES.COMPANY_SECRETARY:
      return buildForm([
        "Form CR13",
        "Cessation Doc",
        "Extract of Board Resolution",
      ]);
    case Constants.APPLICANT_CAPACITIES.AUTHORIZED_PERSON:
      return buildForm(["Form CR9", "Extract of Board Resolution"]);
    case Constants.APPLICANT_CAPACITIES.AUDITOR:
      return buildForm(["Form CR43", "Extract of Board Resolution"]);
    case Constants.APPLICANT_CAPACITIES.ADMINISTRATOR:
      return buildForm(["Form CR46", "Extract of Board Resolution"]);
  }
};

// condition for BOF3
const changedFromReportableToNonReportable = (transaction) => {
  return false;
};

// condition for BOF2
const changesExistForRepotables = (transaction) => {
  return false;
};

// condition for BOF1
const hasBecomeReportable = (transaction) => {
  return false;
};

const addConditionalBOFforms = (forms = [], transaction = {}) => {
  if (changedFromReportableToNonReportable(transaction)) {
    forms.push("Form BOF3");
  }
  if (changesExistForRepotables(transaction)) {
    forms.push("Form BOF2");
  }
  if (hasBecomeReportable(transaction)) {
    forms.push("Form BOF1");
  }
  return forms;
};

const allotmentAndAuthorization = (transaction = {}) => {
  let forms = [
    "Form CR19",
    "Statement of Increase of Nominal Capital",
    "Ardhisasa Invoice Receipt",
    "Proof of Payment of Stamp Duty",
    "Form CR20",
    "Extract of Board Resolution",
  ];
  forms = addConditionalBOFforms(forms, transaction);
  return buildForm(forms);
};

const unIssuedShares_ExistingAuthorization_PVTwithOneClass = (
  transaction = {}
) => {
  let forms = [
    "Statement of Increase of Nominal Capital",
    "Ardhisasa Invoice Receipt",
    "Proof of Payment of Stamp Duty",
    "Form CR20",
    "Extract of Board Resolution",
  ];
  forms = addConditionalBOFforms(forms, transaction);
  return buildForm(forms);
};

const authorization = () => {
  return buildForm(["Form CR19"]);
};

const allotment = () => {
  return buildForm([
    "Statement of Increase of Nominal Capital",
    "Ardhisasa Invoice Receipt",
    "Proof of Payment of Stamp Duty",
    "Form CR20",
    "Form BOF1",
    "Form BOF2",
  ]);
};

const shareTransferForms = [
  "Share Transfer Form",
  "Ardhisasa Invoice Receipt",
  "Proof of Payment of Stamp Duty",
  "Extract of Board Resolutions",
];

const shareTransfer = (transaction) => {
  let forms = shareTransferForms;
  forms = addConditionalBOFforms(forms, transaction);
  return buildForm(forms);
};

const transmissionOfShares = (transaction) => {
  let forms = [...shareTransferForms, "Transmission Notice"];
  forms = addConditionalBOFforms(forms, transaction);
  return buildForm(forms);
};

const forFeitureOfShares = (transaction) => {
  return buildForm([
    ...shareTransferForms,
    "Notice of Forfeiture",
    "Statutory Declaration",
    "Form BOF1",
  ]);
};

const surrenderOfShares = (transaction) => {
  return buildForm(
    [...shareTransferForms],
    "Notice of Surrender by Shareholder"
  );
};

const sharesBuyBack = (transaction) => {
  return buildForm(["Form CR37", "Form CR38", "Form CR19"]);
};

const takeoverOffer = (transaction) => {
  let forms = [...shareTransferForms, "Form CR49"];
  forms = addConditionalBOFforms(forms, transaction);
  return buildForm(forms);
};

const cancellationOfTreasuryShares = (transaction) => {
  return buildForm(["Form CR2", "Form CR19", "Extract of Board Resolution"]);
};

const saleTransferOfTreasuryShares = (transaction) => {
  return buildForm([...shareTransferForms]);
};

const noticeToCourt = () => {
  return buildForm(["Notice to Court"]);
};

const shareholdingSharemanagementForms = ["Form CR34", "Form CR19"];

const sharesRedemption = (transaction) => {
  return buildForm([
    ...shareholdingSharemanagementForms,
    "Extract of Board Resolution",
  ]);
};

const sharesConsolidation = (transaction) => {
  return buildForm(shareholdingSharemanagementForms);
};

const sharesSubdivision = (transaction) => {
  return buildForm(shareholdingSharemanagementForms);
};

const noticeOfChangeOfNameOfClassOfShares = (transaction) => {
  return buildForm(["Form CR32", "Form CR19"]);
};

const noticeOfParticularsOfVariationOfClassOfShares = (transaction) => {
  return buildForm(["Form CR33", "Form CR19"]);
};

const reductionOfShareCapitalSolvency = (transaction) => {
  return buildForm(["Form CR35", "Solvency Statement", "Financial Statement"]);
};

const reductionOfShareCapitalCourt = (transaction) => {
  return buildForm(["Form CR35", "Court Order"]);
};

const changeOfRegisteredAddress = () => {
  return buildForm(["Form CR16", "Extract of Board Resolution"]);
};

const changeOfArticles = () => {
  return buildForm(["Form CR19", "Updated AoA"]);
};

const changeOfName = () => {
  return buildForm([
    "Name Reservation Confirmation",
    "Form CR15",
    "Form CR19",
    "Certificate of Change of Name",
  ]);
};

const changeOfFY = () => {
  return buildForm(["Form CR42", "Extract of Board Resolution"]);
};
const strikeOff = (transaction) => {
  return buildForm(["Form CR18"], transaction);
};

const conversion = () => {
  return buildForm([
    "Form CR15",
    "Form CR17",
    "Form CR19",
    "Ammended Articles of Association",
  ]);
};

const yyyyAnnualReturns = () => {
  return buildForm(["Form CR29", "Appendix for Members List"]);
};

const yyyyFincialStatements = () => {
  return buildForm(["Financial Statements"]);
};

const noticeToCancelConversion = (transaction) => {
  return buildForm(["Form CR30"], transaction);
};

const noticeOfWithdrawalOfStrikeOff = () => {
  return buildForm(["Form CR47", "Form CR19"]);
};

// `company` is passed in from the call site (retrieved via useSelector in the
// calling React component) so this plain utility function stays hook-free.
const administrativeRestoration = (transaction, company) => {
  const includeFormCR29 = isAnnualReturnsDueAfterCompanyStruckOff(company);
  return buildForm(["Form CR48", ...(includeFormCR29 ? ["Form CR29"] : [])]);
};

const debentureAndChargesRegistration = () => {
  return buildForm(["Form CR25", "Form CR27"]);
};

const debentureAndChargesAmendment = () => {
  return buildForm(["Form CR26"]);
};

const debentureAndChargesDischarge = () => {
  return buildForm(["Form CR28"]);
};

export const getDocumentsAsPerTypeOfChange = (
  transaction = {},
  changedFields = {},
  company = null
) => {
  const { changeType } = transaction;
  switch (changeType) {
    /** Officials */
    case Constants.TRANSACTION_CHANGE_TYPES.APPOINTMENT:
      return appointment(transaction);
    case Constants.TRANSACTION_CHANGE_TYPES.CESSATION:
      return cessation(transaction);
    case Constants.TRANSACTION_CHANGE_TYPES.CHANGE_OF_PARTICULARS:
      return changeOfParticulars(transaction, changedFields);

    /** Share capital */
    case Constants.TRANSACTION_CHANGE_TYPES.SHARE_TRANSFER:
      return shareTransfer(transaction);
    case Constants.TRANSACTION_CHANGE_TYPES.TRANSMISSION_OF_SHARES:
      return transmissionOfShares(transaction);
    case Constants.TRANSACTION_CHANGE_TYPES.SURRENDER_OF_SHARES:
      return surrenderOfShares(transaction);
    case Constants.TRANSACTION_CHANGE_TYPES.SHARES_BUYBACK:
      return sharesBuyBack(transaction);
    case Constants.TRANSACTION_CHANGE_TYPES.CANCELATION_OF_TREASURY_SHARES:
      return cancellationOfTreasuryShares(transaction);
    case Constants.TRANSACTION_CHANGE_TYPES.SALE_TRANSFER_OF_TREASURY_SHARES:
      return saleTransferOfTreasuryShares(transaction);
    case Constants.TRANSACTION_CHANGE_TYPES.SHARES_REDEMPTION:
      return sharesRedemption(transaction);
    case Constants.TRANSACTION_CHANGE_TYPES.NOTICE_TO_COURT:
      return noticeToCourt(transaction);
    case Constants.TRANSACTION_CHANGE_TYPES.TAKEOVER_OFFER:
      return takeoverOffer(transaction);
    case Constants.TRANSACTION_CHANGE_TYPES.SHARES_CONSOLIDATION:
      return sharesConsolidation(transaction);
    case Constants.TRANSACTION_CHANGE_TYPES.SHARES_SUBDIVISION:
      return sharesSubdivision(transaction);
    case Constants.TRANSACTION_CHANGE_TYPES.NOTICE_OF_CHANGE_OF_NAME:
      return noticeOfChangeOfNameOfClassOfShares(transaction);
    case Constants.TRANSACTION_CHANGE_TYPES.NOTICE_OF_PARTICULARS:
      return noticeOfParticularsOfVariationOfClassOfShares(transaction);
    case Constants.TRANSACTION_CHANGE_TYPES.SOLVENCY_STATEMENT:
      return reductionOfShareCapitalSolvency(transaction);
    case Constants.TRANSACTION_CHANGE_TYPES.COURT_ORDER:
      return reductionOfShareCapitalCourt(transaction);
    case Constants.TRANSACTION_CHANGE_TYPES.AUTHORIZATION:
      return authorization(transaction);
    case Constants.TRANSACTION_CHANGE_TYPES.ALLOTMENT:
      return allotment(transaction);
    case Constants.TRANSACTION_CHANGE_TYPES.AUTHORIZATION_AND_ALLOTMENT:
      return allotmentAndAuthorization(transaction);

    /** Company Changes */
    case Constants.TRANSACTION_CHANGE_TYPES.CHANGE_OF_REGISTERED_ADDRESS:
      return changeOfRegisteredAddress();
    case Constants.TRANSACTION_CHANGE_TYPES.CHANGE_OF_ARTICLES:
      return changeOfArticles();
    case Constants.TRANSACTION_CHANGE_TYPES.CHANGE_OF_NAME:
      return changeOfName();
    case Constants.TRANSACTION_CHANGE_TYPES.CHANGE_OF_FY:
      return changeOfFY();
    case Constants.TRANSACTION_CHANGE_TYPES.STRIKE_OFF:
      return strikeOff(transaction);
    case Constants.TRANSACTION_CHANGE_TYPES.CONVERSION:
      return conversion();
    case Constants.TRANSACTION_CHANGE_TYPES.YYYY_ANNUAL_RETURNS:
      return yyyyAnnualReturns();
    case Constants.TRANSACTION_CHANGE_TYPES.YYYY_FINANCIAL_STATEMENTS:
      return yyyyFincialStatements();
    case Constants.TRANSACTION_CHANGE_TYPES.NOTICE_TO_CANCEL_CONVERSION:
      return noticeToCancelConversion(transaction);
    case Constants.TRANSACTION_CHANGE_TYPES.NOTICE_OF_WITHDRAWAL_OF_STRIKE_OFF:
      return noticeOfWithdrawalOfStrikeOff();
    case Constants.TRANSACTION_CHANGE_TYPES.ADMINISTRATIVE_RESTORATION:
      return administrativeRestoration(transaction, company);
    case Constants.TRANSACTION_CHANGE_TYPES.DEBENTURE_AND_CHARGES_REGISTRATION:
      return debentureAndChargesRegistration();
    case Constants.TRANSACTION_CHANGE_TYPES.DEBENTURE_AND_CHARGES_AMENDMENT:
      return debentureAndChargesAmendment();
    case Constants.TRANSACTION_CHANGE_TYPES.DEBENTURE_AND_CHARGES_DISCHARGE:
      return debentureAndChargesDischarge();

    default:
      return buildForm(["Unhandled Doc"]);
  }
};

export const brsFilingReceipt = () => {
  return [
    {
      label: "BRS Filing Receipt",
      name: "brsFilingReceipt",
      accept: "application/pdf",
    },
  ];
};

export const courtOrderDocuments = () => {
  return [
    {
      label: "Court Order",
      name: "courtOrder",
      accept: "application/pdf",
    },
  ];
};